import {
  Button,
  Collapse,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stack,
  Text,
  Input,
  VStack
} from '@chakra-ui/react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { format, set, add } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';

import { SFPlusCircleIcon, SFCalendarIcon } from '@assets/icons';

import { useUserStore } from '@store/app.store';
import { useStyledToast } from '@util/hooks';
import { maxFileSizeBytes, createFileValidator } from '@util/helpers';
import { useInitiativeOptions } from '@services/query-services/initiatives.query-service';
import CustomSelect from '@components/general/form/custom-select.component';
import ReskinCalendar from '@components/general/reskin-calendar.component';
import S3Bucket from '@services/s3bucket.service';
import { usePostConvertFromVideo } from '@services/query-services/lambda.query-service';

import StepWithLabelComponent from '@components/general/form/step-with-label.component';
import DocumentDropzone from './document-dropzone.component';

import {
  MAX_AV_FILE_SIZE_MB,
  ACCEPTED_AV_FILE_FORMATS,
  MAX_TRANSCRIPT_FILE_SIZE_MB,
  ACCEPTED_TRANSCRIPT_FILE_FORMATS
} from '@util/constants';

const SummarizeAudioVideo = ({ disclosure, meeting = null }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const queryClient = useQueryClient();
  const toast = useStyledToast();
  const user = useUserStore((state) => state.user);
  const tenant = useUserStore((state) => state.tenant);
  const getSessionId = useUserStore((state) => state?.getSessionId);
  const { data: focusOptions } = useInitiativeOptions({ type: 'Focus' });
  const { mutateAsync: convertVideoToMeeting } = usePostConvertFromVideo();

  const {
    handleSubmit,
    control,
    register,
    watch,
    formState: { errors },
    setError,
    clearErrors,
    getFieldState,
    reset: resetForm
  } = useForm({
    defaultValues: {
      date: meeting?.start_at ? new Date(meeting.start_at) : new Date(),
      time: meeting?.start_at
        ? format(new Date(meeting.start_at), 'HH:mm')
        : format(new Date(), 'HH:mm'),
      title: meeting?.title || ''
    }
  });

  const file = watch('file');
  const transcriptFile = watch('transcriptFile');
  const validateFile = createFileValidator({ maxSizeMB: MAX_AV_FILE_SIZE_MB });
  const validateTranscriptFile = createFileValidator({ maxSizeMB: MAX_TRANSCRIPT_FILE_SIZE_MB });

  const tabProps = {
    color: 'brandPrimary.primary',
    borderRadius: 'xl',
    px: 4,
    py: 3,
    fontWeight: 500,
    _selected: { bg: 'brandPrimary.onContainer', color: 'brandPrimary.onPrimary' },
    whiteSpace: 'nowrap'
  };

  const onSubmit = async (formValues) => {
    setIsSubmitting(true);
    if (!file && !transcriptFile && !formValues.link) {
      setError('file', { type: 'required', message: 'Add a file, transcript, or link' });
      setIsSubmitting(false);
      return;
    }

    const [hours, minutes] = formValues.time.split(':');
    const startDateTime = set(new Date(formValues.date), {
      hours,
      minutes,
      seconds: 0,
      milliseconds: 0
    });

    if (startDateTime > new Date()) {
      setError('time', { message: 'Time must not be in the future' });
      setIsSubmitting(false);
      return;
    }

    const endDateTime = add(startDateTime, { hours: 1 });
    const payloadToSend = {
      session_id: getSessionId(),
      name: formValues.title,
      start_at: startDateTime,
      end_at: endDateTime,
      is_public_url: !file && !transcriptFile,
      tenant_id: tenant?.id,
      creator_id: user?.id,
      initiative_id: formValues?.initiative?.value
    };

    if (meeting) {
      payloadToSend.meeting_id = meeting.id;
    }

    if (file || transcriptFile) {
      const s3 = new S3Bucket('fora-meeting-uploads', {
        queryClient,
        user,
        tenant
      });
      const fileToUpload = file || transcriptFile;
      payloadToSend.file_location = await s3.uploadFile(fileToUpload);
      if (transcriptFile) {
        payloadToSend.file_type = 'transcript';
        payloadToSend.meeting_config = { tags: ['transcript_only'] };
      }
    } else {
      payloadToSend.file_location = formValues.link;
    }

    await convertVideoToMeeting(payloadToSend, {
      onSuccess: async () => {
        toast({
          title: 'Upload success, processing initiated',
          status: 'success',
          duration: 9000,
          isClosable: true
        });
        setIsSubmitting(false);
        disclosure.onClose();
      },
      onError: () => {
        toast({ status: 'error', title: 'Failed to upload meeting' });
        setIsSubmitting(false);
      }
    });
  };

  return (
    <Modal
      {...disclosure}
      variant='classic'
      onCloseComplete={() => {
        resetForm();
        setIsSubmitting(false);
      }}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <SFPlusCircleIcon
              boxSize={7}
              p={1}
            />
            <Text variant='body-600-md'>
              {meeting ? `Attach data to meeting: ${meeting?.title}` : 'Summarize Meeting'}
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={11}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={6}>
              <Stack gap={3}>
                <Tabs
                  variant='enclosed'
                  align='center'
                  isFitted
                >
                  <TabList
                    display='inline-flex'
                    gap={1}
                    bg='brandPrimary.containerLow'
                    p={1}
                    borderRadius='xl'
                  >
                    <Tab
                      {...tabProps}
                      data-testid='upload-file-tab'
                    >
                      Upload File
                    </Tab>
                    <Tab
                      {...tabProps}
                      data-testid='add-link-tab'
                    >
                      Add Link
                    </Tab>
                    <Tab
                      {...tabProps}
                      data-testid='upload-transcript-tab'
                    >
                      Upload Transcript
                    </Tab>
                  </TabList>
                  <TabPanels
                    p={0}
                    mt={3}
                  >
                    <TabPanel
                      p={0}
                      data-testid='document-tab-panel'
                    >
                      <Controller
                        control={control}
                        name='file'
                        render={({ field: { value, onChange } }) => (
                          <DocumentDropzone
                            file={value}
                            onFileLoad={(file) => onChange(file)}
                            onFileRemove={() => {}}
                            validateFile={validateFile}
                            maxFileSize={maxFileSizeBytes(MAX_AV_FILE_SIZE_MB)}
                            helperText={{
                              heading: 'Drag & drop audio or video files here',
                              subtext: 'Upload MP3 or MP4 (Max size: 2GB)'
                            }}
                            accept={ACCEPTED_AV_FILE_FORMATS}
                            maxFiles={1}
                            onError={(error) => {
                              if (!error) {
                                clearErrors('file');
                              } else {
                                setError('file', { message: error.message });
                              }
                            }}
                          />
                        )}
                      />
                    </TabPanel>
                    <TabPanel
                      p={0}
                      mt={3}
                      data-testid='link-tab-panel'
                    >
                      <VStack
                        bgColor='brandPrimary.containerLow'
                        borderRadius='lg'
                        minH='200px'
                        p={7}
                        display='flex'
                        justify='center'
                        flexDir='column'
                        spacing={0}
                        align='stretch'
                      >
                        <StepWithLabelComponent
                          label='Link to a meeting recording'
                          name='link'
                          fieldState={getFieldState('link')}
                        >
                          <Input
                            placeholder='Enter a supported link'
                            data-testid='link-input'
                            {...register('link', {
                              maxLength: {
                                value: 512,
                                message: 'Link must be 512 characters or fewer'
                              },
                              pattern: {
                                value: /^(https):\/\/[\w.\-/!:#?=&%,@]+$/i,
                                message: 'Please enter a valid URL'
                              }
                            })}
                          />
                          <Text
                            align='left'
                            variant='body-sm'
                            color='surface.outline'
                          >
                            Must be from a supported site (Zoom, Teams, Meeting, AWS). Need Help?
                          </Text>
                        </StepWithLabelComponent>
                      </VStack>
                    </TabPanel>
                    <TabPanel
                      p={0}
                      data-testid='transcript-tab-panel'
                    >
                      <Controller
                        control={control}
                        name='transcriptFile'
                        render={({ field: { value, onChange } }) => (
                          <DocumentDropzone
                            file={value}
                            onFileLoad={(file) => onChange(file)}
                            onFileRemove={() => {}}
                            validateFile={validateTranscriptFile}
                            maxFileSize={maxFileSizeBytes(MAX_TRANSCRIPT_FILE_SIZE_MB)}
                            helperText={{
                              heading: 'Drag & drop transcript files here',
                              subtext: 'Upload VTT files (Max size: 50MB)'
                            }}
                            accept={ACCEPTED_TRANSCRIPT_FILE_FORMATS}
                            maxFiles={1}
                            onError={(error) => {
                              if (!error) {
                                clearErrors('transcriptFile');
                              } else {
                                setError('transcriptFile', { message: error.message });
                              }
                            }}
                          />
                        )}
                      />
                    </TabPanel>
                  </TabPanels>
                </Tabs>

                <Collapse in={!!errors?.file || !!errors?.transcriptFile}>
                  <Text
                    color='critical.primary'
                    variant='label-600-md'
                  >
                    {errors?.file?.message || errors?.transcriptFile?.message}
                  </Text>
                </Collapse>
              </Stack>

              <HStack w='100%'>
                <Controller
                  control={control}
                  name='date'
                  render={({ field: { onChange, value } }) => (
                    <StepWithLabelComponent
                      label='Meeting Date'
                      name='date'
                      fieldState={getFieldState('date')}
                      stackProps={{ flex: 1 }}
                    >
                      <ReskinCalendar
                        trigger={
                          <Button
                            variant='reskinWhite'
                            size='reskinLg'
                            rightIcon={<SFCalendarIcon boxSize='13px' />}
                            justifyContent='space-between'
                            w='100%'
                            isDisabled={!!meeting}
                          >
                            {format(value, 'PP')}
                          </Button>
                        }
                        date={value}
                        maxDate={new Date()}
                        setDate={onChange}
                        fadePastDates={false}
                        placement='top'
                        strategy='fixed'
                        popoverContentProps={{ ml: { base: 3, lg: 'unset' } }}
                        isDisabled={!!meeting}
                      />
                    </StepWithLabelComponent>
                  )}
                />

                <StepWithLabelComponent
                  label='Meeting Time'
                  name='time'
                  fieldState={getFieldState('time')}
                  stackProps={{ flex: 1 }}
                >
                  <Input
                    type='time'
                    placeholder='12:30PM'
                    data-testid='meeting-time-input'
                    isDisabled={!!meeting}
                    {...register('time', { required: 'Pick a time' })}
                  />
                </StepWithLabelComponent>
              </HStack>

              <StepWithLabelComponent
                label='Title'
                name='title'
                fieldState={getFieldState('title')}
              >
                <Input
                  placeholder='Your meeting title'
                  data-testid='meeting-title-input'
                  isDisabled={!!meeting}
                  {...register('title', { required: 'Add a meeting title' })}
                />
              </StepWithLabelComponent>

              {!meeting && (
                <Controller
                  control={control}
                  name='initiative'
                  rules={{ required: 'Pick a category' }}
                  render={({ field: { name, value, onChange }, fieldState }) => (
                    <StepWithLabelComponent
                      label='Category'
                      name={name}
                      fieldState={fieldState}
                    >
                      <CustomSelect
                        size='md'
                        isMulti={false}
                        placeholder='Choose Category'
                        options={focusOptions}
                        value={value}
                        onChange={onChange}
                        menuPlacement='top'
                      />
                    </StepWithLabelComponent>
                  )}
                />
              )}

              <Button
                alignSelf='end'
                type='submit'
                variant='reskinPrimary'
                size='reskinLg'
                borderRadius='lg'
                isLoading={isSubmitting}
                data-testid='summarize-meeting-button'
              >
                Summarize
              </Button>
            </Stack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default SummarizeAudioVideo;
